import React, { FunctionComponent, Suspense, memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

import { HashLink } from "../../navigation/HashLink";
import { createDealerLink } from "../../navigation/createDealerLink";
import { useAuth } from "../../stores/hooks/useAuth";
import { HashtagString, createHashStateHook } from "../../stores/useHashState";
import { RoundButton } from "../../ui/Button/Button";
import { Dropdown } from "../../ui/Dropdown";
import { MenuButton } from "../../ui/Menu/MenuButton";
import { UserIcon } from "../../ui/icons/UserIcon";
import { usePackageEditorSetting } from "../../util/usePackageEditorSetting";
import {
  myLeadsHash,
  myOrdersHash,
  myQuotesHash,
} from "../MyQuotes/QuotesDialog";
import { useCmsLink } from "./useCmsLink";

const MyQuotes = React.lazy(() => import("../MyQuotes/QuotesDialog"));

const userMenuHash: HashtagString = "#user-menu";

const useUserMenuState = createHashStateHook(userMenuHash);

const Uncached: FunctionComponent = () => {
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const [{ tokenId }, { isSigningOut, signOut }] = useAuth();
  const [userMenuIsVisible, { hide: hideUserMenu, show: showUserMenu }] =
    useUserMenuState();
  const cmsLink = useCmsLink();
  const hasPackageEditor = usePackageEditorSetting();
  const packageEditorLink = createDealerLink(
    t("packageEditor.packageEditor"),
    "package-editor"
  );
  return (
    <>
      <RoundButton
        aria-controls="user-menu"
        aria-expanded={userMenuIsVisible}
        aria-haspopup="true"
        aria-label="openUserMenu"
        disabled={userMenuIsVisible}
        id="user-menu-button"
        onClick={showUserMenu}
        ref={menuButtonRef}
      >
        <UserIcon />
      </RoundButton>
      <Dropdown
        aria-labelledby="user-menu-button"
        className="py-2 min-w-[12rem]"
        id="user-menu"
        onClose={hideUserMenu}
        preferredPlacement="bottom-right"
        producerRef={menuButtonRef}
        role="menu"
        visible={userMenuIsVisible}
      >
        <HashLink hash={myLeadsHash}>
          <MenuButton
            aria-label="openMyLeadsDialog"
            element="a"
            id="my-leads-link"
          >
            {t("auth.myLeads")}
          </MenuButton>
        </HashLink>
        <HashLink hash={myQuotesHash}>
          <MenuButton
            aria-label="openMyQuotesDialog"
            element="a"
            id="my-quotes-link"
          >
            {t("auth.myQuotes")}
          </MenuButton>
        </HashLink>
        <HashLink hash={myOrdersHash}>
          <MenuButton
            aria-label="openMyOrderDialog"
            element="a"
            id="my-orders-link"
          >
            {t("auth.myOrders")}
          </MenuButton>
        </HashLink>
        {hasPackageEditor && (
          <Link href={packageEditorLink.to}>
            <MenuButton
              aria-label="openPackageEditor"
              element="a"
              id="package-editor-link"
              selected={packageEditorLink.isActive}
            >
              {packageEditorLink.name}
            </MenuButton>
          </Link>
        )}
        <hr className="mx-4 my-2" />
        <MenuButton
          aria-label="openCMS"
          element="a"
          href={cmsLink}
          id="cms-link"
          target="_blank"
        >
          {t("siteHeader.cmsUrl")}
        </MenuButton>
        <MenuButton
          aria-label="openBookingSystem"
          element="a"
          // NOTE: Same as code/client/service/UtilityService.js:getBookingUrl
          href={`${window.cmsBaseUrl}/authentication/sso/retailSite.php?tokenId=${tokenId}`}
          id="booking-system-link"
          target="_blank"
        >
          {t("siteHeader.bookinkSystem")}
        </MenuButton>
        <MenuButton
          aria-label="openHowtoVideos"
          element="a"
          href="https://www.youtube.com/playlist?list=PLmEy9liVWF0t_XWyWb1fWS_a8B_Cd2JKJ"
          id="how-to-videos-link"
          rel="noopener noreferrer"
          target="_blank"
        >
          {t("siteHeader.howToVideos")}
        </MenuButton>
        <hr className="mx-4 my-2" />
        <MenuButton
          aria-label="signOut"
          busy={isSigningOut}
          id="sign-out-button"
          onClick={signOut}
        >
          {t("siteHeader.signOut")}
        </MenuButton>
      </Dropdown>
      <Suspense fallback={null}>
        <MyQuotes />
      </Suspense>
    </>
  );
};

export const UserButton = memo(Uncached);
